import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, ExternalLink, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { debriefs } from "@/lib/api";
import { MYVAC_PROFILE_URL } from "@/lib/links";

/**
 * A filed flight's debrief: the same image the share link shows (drawn once on
 * the server, backend/debrief_card.py), the actions, and what only the pilot
 * sees (anti-cheat notes). `debrief` is a /pilot/debriefs/... response.
 */
export default function DebriefCard({ debrief }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const id = debrief?.logbook_id;

  useEffect(() => {
    if (!id) return undefined;
    let url = null;
    let live = true;
    setSrc(null);
    setFailed(false);
    debriefs.image(id)
      .then((blob) => { if (live) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => live && setFailed(true));
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [id]);

  if (!debrief) return null;
  const d = debrief.data || {};
  const flags = Array.isArray(d.flags) ? d.flags : [];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(debrief.share_url);
      toast.success("Link copied. Paste it in Discord to show the card.");
    } catch {
      toast.error("Couldn't copy. The link is: " + debrief.share_url);
    }
  };

  const btn = "text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary flex items-center gap-2";

  return (
    <div className="card-flat p-4" data-testid="debrief-card">
      <div className="rounded-lg overflow-hidden border border-border bg-black/40 aspect-[1200/630] flex items-center justify-center">
        {src ? (
          <img src={src} alt={`Debrief: ${d.orig} to ${d.dest}`} className="w-full h-full object-contain" />
        ) : failed ? (
          <span className="text-sm text-muted-foreground">The card couldn't be drawn.</span>
        ) : (
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {debrief.shared ? (
          <>
            <button type="button" onClick={copy} className={btn} data-testid="debrief-copy">
              <Copy className="w-3.5 h-3.5" /> Copy share link
            </button>
            <a href={debrief.share_url} target="_blank" rel="noreferrer" className={btn}>
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
          </>
        ) : (
          <span className="text-xs text-muted-foreground self-center" data-testid="debrief-private">
            Sharing is off. Turn it on in <Link to="/settings" className="text-primary hover:underline">Settings</Link> to get a link.
          </span>
        )}
        {src && (
          <a href={src} download={`debrief-${d.flt || id}-${d.orig}-${d.dest}.png`} className={btn}>
            <Download className="w-3.5 h-3.5" /> Save image
          </a>
        )}
        {/* myVAC's logbook marks every flight with a debrief. */}
        <a href={MYVAC_PROFILE_URL} target="_blank" rel="noreferrer" className={btn} data-testid="debrief-myvac">
          <ExternalLink className="w-3.5 h-3.5" /> Open in myVAC
        </a>
      </div>

      {flags.length > 0 && (
        <div className="mt-4 text-xs border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2 flex gap-2" data-testid="debrief-flags">
          <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0" />
          <div>
            <div className="font-bold mb-0.5">Flagged for review (only you see this)</div>
            {flags.map((f, i) => <div key={i}>{typeof f === "string" ? f : JSON.stringify(f)}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}
